import { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Card, Descriptions, Form, Input, Popconfirm, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { apiErrorMessage } from '@/api/client';
import {
  getAirwallexSettings,
  saveAirwallexSettings,
  testAirwallexSettings,
  deleteAirwallexSettings,
  type AirwallexSettings,
} from '@/api/settings';

const { Paragraph, Text } = Typography;

/**
 * 🔴 未配置时预填**沙箱**，不是生产。方向不对称：默认沙箱最坏是打不成、当场看得见；
 * 默认生产最坏是有人以为在测试，钱却真的出去了。与后端的 DEFAULT_BASE_URL 一致。
 */
const SANDBOX_BASE_URL = 'https://api.sandbox.airwallex.com';

/** 沙箱判定与后端无关，只用于界面提示，所以放宽到「地址里带 sandbox 或 demo」。 */
const isSandboxUrl = (u?: string) => Boolean(u && /sandbox|demo/i.test(u));

/**
 * 设置页。当前只有一块：本组织的 Airwallex 凭据。**仅管理员可见可改**
 * （路由层已经拦了，见 router.tsx 的 RequireAdmin）。
 *
 * 🔴 密钥是**只写**的：后端永远只回掩码尾号，界面上没有任何地方能读回明文。
 * 要换就整条覆盖重填，这比「显示出来让人核对」安全得多。
 */
export default function Settings() {
  const { message } = App.useApp();
  const [data, setData] = useState<AirwallexSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm<{ clientId: string; apiKey: string; baseUrl: string; loginAs?: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAirwallexSettings();
      setData(res);
      // 换凭据时把当前环境带进表单：这一栏是「保持原样还是要换环境」，不该每次从空白开始
      form.setFieldValue('baseUrl', res.baseUrl || SANDBOX_BASE_URL);
      form.setFieldValue('loginAs', res.loginAs || '');
      // 未配置时直接展开表单，省掉一次「点击开始配置」
      if (!res.configured) setEditing(true);
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message, form]);

  useEffect(() => {
    // 取数是 effect 的正当用途（与外部系统同步）
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  async function save() {
    let values: { clientId: string; apiKey: string; baseUrl: string; loginAs?: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      // 后端保存前会实地打一发 Airwallex 登录，凭据不对根本存不进去
      const res = await saveAirwallexSettings({
        clientId: values.clientId.trim(),
        apiKey: values.apiKey.trim(),
        baseUrl: values.baseUrl.trim(),
        ...(values.loginAs?.trim() ? { loginAs: values.loginAs.trim() } : {}),
      });
      setData(res);
      setEditing(false);
      form.resetFields();
      message.success('已保存，凭据验证通过');
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    try {
      await testAirwallexSettings();
      message.success('连接正常');
      void load();
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setTesting(false);
    }
  }

  async function remove() {
    try {
      await deleteAirwallexSettings();
      message.success('已删除');
      form.resetFields();
      void load();
    } catch (err) {
      message.error(apiErrorMessage(err));
    }
  }

  const isSandbox = isSandboxUrl(data?.baseUrl);

  return (
    <Card title="设置" loading={loading} style={{ maxWidth: 720 }}>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        空中云汇（Airwallex）
      </Typography.Title>
      <Paragraph type="secondary">
        填入本组织自己的 Airwallex API 凭据后，才能创建收款人。
        <b>凭据加密存储，保存后无法再读出明文</b>，只能整条覆盖。
      </Paragraph>

      <Alert
        style={{ marginBottom: 16 }}
        type={isSandbox ? 'warning' : 'info'}
        showIcon
        title={
          <>
            当前环境：<Text code>{data?.baseUrl}</Text>
            {isSandbox ? '（沙箱，不会产生真实资金流动）' : '（生产，这里打出去的是真实资金）'}
          </>
        }
        description="环境由本组织自己的「API 根地址」决定，与其它组织无关。沙箱凭据在生产环境不可用，反之亦然。"
      />

      {data?.configured && !editing && (
        <>
          <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="状态">
              <Tag color="green">已配置</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="API 根地址">
              <Text code>{data.baseUrl}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Client ID">
              <Text code>{data.clientId}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="目标账户 ID（loginAs）">
              {data.loginAs ? <Text code>{data.loginAs}</Text> : '未指定'}
            </Descriptions.Item>
            <Descriptions.Item label="API Key">
              <Text code>{data.apiKeyHint}</Text>
              <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                （加密存储，不可读回）
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="最近验证">
              {data.lastVerifiedAt ? dayjs(data.lastVerifiedAt).format('YYYY-MM-DD HH:mm') : '—'}
            </Descriptions.Item>
          </Descriptions>
          <Space>
            <Button onClick={() => {
              form.setFieldsValue({ baseUrl: data.baseUrl, loginAs: data.loginAs || '' });
              setEditing(true);
            }}>更换凭据</Button>
            <Button loading={testing} onClick={test}>
              测试连接
            </Button>
            <Popconfirm
              title="确认删除凭据？"
              description="删除后本组织将无法创建收款人，已创建的不受影响。"
              onConfirm={remove}
            >
              <Button danger>删除</Button>
            </Popconfirm>
          </Space>
        </>
      )}

      {editing && (
        <Form form={form} layout="vertical">
          <Form.Item
            name="baseUrl"
            label="API 根地址"
            rules={[{ required: true, message: '请输入 API 根地址' }]}
            extra="沙箱 https://api.sandbox.airwallex.com，生产 https://api.airwallex.com。只能填 airwallex.com 及其子域。"
          >
            <Input autoComplete="off" placeholder={SANDBOX_BASE_URL} />
          </Form.Item>
          <Form.Item
            name="clientId"
            label="Client ID"
            rules={[{ required: true, message: '请输入 Client ID' }]}
            extra="Airwallex 控制台 → Developer → API keys 里的 Client ID"
          >
            <Input autoComplete="off" placeholder="例如 EXAMPLE-ClientId-000000" />
          </Form.Item>
          <Form.Item
            name="loginAs"
            label="目标账户 ID（loginAs）"
            rules={[{ max: 128, message: '目标账户 ID 最多 128 个字符' }]}
            extra="多账户 API Key 必须指定实际付款账户的 Account ID，可在 Airwallex 设置 → 账户详情中查看；单账户 Key 可留空。"
          >
            <Input autoComplete="off" placeholder="目标账户的 Account ID" />
          </Form.Item>
          <Form.Item
            name="apiKey"
            label="API Key"
            rules={[{ required: true, message: '请输入 API Key' }]}
            extra="只在这里填一次。保存时会实地验证一次连通性，验证不通过不会存。"
          >
            {/* Password 而不是 Input：避免密钥出现在录屏、截图和肩窥里 */}
            <Input.Password autoComplete="new-password" placeholder="粘贴 API Key" />
          </Form.Item>
          <Space>
            <Button type="primary" loading={saving} onClick={save}>
              保存并验证
            </Button>
            {data?.configured && (
              <Button
                onClick={() => {
                  setEditing(false);
                  form.resetFields();
                }}
              >
                取消
              </Button>
            )}
          </Space>
        </Form>
      )}
    </Card>
  );
}
